import React, { useMemo, useState } from 'react';
import { FeedEvent } from '../../types';
import s from './ActivityFeed.module.css';

// ── Categories (drive the filter chips) ──────────────────────────────────────
type Cat = 'all' | 'alert' | 'nav' | 'click' | 'chat' | 'tracked';

const CATS: { id: Cat; icon: string; label: string; color: string }[] = [
  { id: 'all',     icon: '📋', label: 'All',        color: '#1a2030' },
  { id: 'alert',   icon: '⚠',  label: 'Alerts',     color: '#e11d48' },
  { id: 'nav',     icon: '📍', label: 'Navigation', color: '#0284c7' },
  { id: 'click',   icon: '👆', label: 'Clicks',     color: '#7c3aed' },
  { id: 'chat',    icon: '💬', label: 'Chat',       color: '#16a34a' },
  { id: 'tracked', icon: '📡', label: 'Tracked',    color: '#64748b' },
];

const ALERT_EVENT_TYPES = new Set([
  'EVENT_UPLOAD_FAIL', 'EVENT_WIZARD_STUCK', 'EVENT_SCREEN_HOLD',
  'EVENT_BACK_FORWARD_REPEAT', 'EVENT_IMPORT_RETRY',
]);

const categoryOf = (ev: FeedEvent): Cat => {
  if (ev.type === 'stuck_alert' || ev.type === 'idle_alert') return 'alert';
  if (ev.needsAttention) return 'alert';
  if (ev.eventType && ALERT_EVENT_TYPES.has(ev.eventType)) return 'alert';
  if (ev.severity === 'error' || ev.severity === 'warning') return 'alert';
  if (ev.type === 'page_visit') return 'nav';
  if (ev.type === 'element_click' || ev.eventType === 'EVENT_CLICK') return 'click';
  if (ev.type === 'user_chat' || ev.type === 'agent_sent' || ev.type === 'flow_sent'
    || ev.type === 'highlight_sent' || ev.type === 'sequence_sent') return 'chat';
  return 'tracked';
};

const sevOf = (ev: FeedEvent): 'error' | 'warning' | 'info' => {
  if (ev.severity) return ev.severity;
  if (ev.type === 'stuck_alert') return 'error';
  if (ev.type === 'idle_alert') return 'warning';
  if (ev.eventType === 'EVENT_UPLOAD_FAIL') return 'error';
  if (ev.eventType && ALERT_EVENT_TYPES.has(ev.eventType)) return 'warning';
  return 'info';
};

// ── Per-type icon / accent colour / badge label ──────────────────────────────
const META: Record<string, { icon: string; color: string; badge?: string }> = {
  page_visit:               { icon: '📍', color: '#0284c7', badge: 'Navigate' },
  element_click:            { icon: '👆', color: '#7c3aed', badge: 'Click' },
  user_chat:                { icon: '💬', color: '#0284c7', badge: 'User' },
  agent_sent:               { icon: '📤', color: '#16a34a', badge: 'Agent' },
  flow_sent:                { icon: '🧭', color: '#16a34a', badge: 'Flow' },
  highlight_sent:           { icon: '✨', color: '#16a34a', badge: 'Highlight' },
  sequence_sent:            { icon: '🧭', color: '#16a34a', badge: 'Sequence' },
  user_joined:              { icon: '🟢', color: '#16a34a', badge: 'Joined' },
  user_left:                { icon: '⚫', color: '#64748b', badge: 'Left' },
  info:                     { icon: 'ℹ️', color: '#64748b' },
  tracked_event:            { icon: '📡', color: '#64748b', badge: 'Tracked' },
  stuck_alert:              { icon: '⚠️', color: '#e11d48', badge: 'Stuck' },
  idle_alert:               { icon: '⏱',  color: '#E08600', badge: 'Idle' },
  // TrackedEvent subtypes
  EVENT_CLICK:              { icon: '👆', color: '#7c3aed', badge: 'Click' },
  EVENT_UPLOAD_START:       { icon: '⬆️', color: '#0284c7', badge: 'Upload' },
  EVENT_UPLOAD_FAIL:        { icon: '❌', color: '#e11d48', badge: 'Upload Fail' },
  EVENT_IMPORT_RETRY:       { icon: '🔄', color: '#E08600', badge: 'Import Retry' },
  EVENT_WIZARD_STUCK:       { icon: '🧩', color: '#E08600', badge: 'Wizard Stuck' },
  EVENT_BACK_FORWARD_REPEAT:{ icon: '↔️', color: '#E08600', badge: 'Back/Forward' },
  EVENT_SCREEN_HOLD:        { icon: '⏳', color: '#E08600', badge: 'Screen Hold' },
};

const getMeta = (ev: FeedEvent) => {
  if (ev.type === 'tracked_event' && ev.eventType && META[ev.eventType]) return META[ev.eventType];
  return META[ev.type] ?? META.info;
};

const describe = (ev: FeedEvent): string | undefined => {
  if (ev.type === 'tracked_event' && ev.eventType) return ev.detail;
  switch (ev.type) {
    case 'page_visit':    return `navigated to ${ev.page ?? ev.detail ?? ''}`;
    case 'element_click': return `clicked ${ev.elementId ?? ev.detail ?? ''}`;
    case 'user_chat':     return `said: "${(ev.detail ?? '').slice(0, 120)}"`;
    case 'user_joined':   return ev.detail ?? 'connected';
    case 'user_left':     return ev.detail ?? 'disconnected';
    case 'flow_sent':     return `guidance flow: ${ev.detail ?? ''}`;
    default:              return ev.detail;
  }
};

// ── Time bucketing ───────────────────────────────────────────────────────────
const BUCKETS: { max: number; label: string }[] = [
  { max: 2  * 60_000, label: 'Just now' },
  { max: 5  * 60_000, label: 'Last 5 minutes' },
  { max: 15 * 60_000, label: 'Last 15 minutes' },
  { max: 60 * 60_000, label: 'Last hour' },
  { max: Infinity,    label: 'Earlier' },
];
const bucketLabel = (ageMs: number) =>
  (BUCKETS.find(b => ageMs < b.max) ?? BUCKETS[BUCKETS.length - 1]).label;

const tsMs = (ev: FeedEvent) => (ev.ts instanceof Date ? ev.ts : new Date(ev.ts)).getTime();
const fmt  = (ev: FeedEvent) =>
  (ev.ts instanceof Date ? ev.ts : new Date(ev.ts))
    .toLocaleTimeString('en', { hour: '2-digit', minute: '2-digit', second: '2-digit' });

const matchesQuery = (ev: FeedEvent, q: string) => {
  if (!q) return true;
  const hay = `${ev.userName} ${ev.page ?? ''} ${ev.detail ?? ''} ${ev.elementId ?? ''} ${ev.eventType ?? ''}`.toLowerCase();
  return hay.includes(q);
};

// ── Component ────────────────────────────────────────────────────────────────
export const ActivityFeed: React.FC<{ events: FeedEvent[]; selectedUserName?: string }> = ({ events, selectedUserName }) => {
  const [filter, setFilter] = useState<Cat>('all');
  const [query,  setQuery]  = useState('');
  const q = query.trim().toLowerCase();

  // Chip counts reflect category totals (independent of the search box).
  const counts = useMemo(() => {
    const c: Record<Cat, number> = { all: events.length, alert: 0, nav: 0, click: 0, chat: 0, tracked: 0 };
    events.forEach(ev => { c[categoryOf(ev)]++; });
    return c;
  }, [events]);

  const list = useMemo(
    () => events.filter(ev => (filter === 'all' || categoryOf(ev) === filter) && matchesQuery(ev, q)),
    [events, filter, q],
  );

  // Group the (already newest-first) list into contiguous time buckets.
  const groups = useMemo(() => {
    const now = Date.now();
    const out: { label: string; items: FeedEvent[] }[] = [];
    list.forEach(ev => {
      const label = bucketLabel(now - tsMs(ev));
      const last = out[out.length - 1];
      if (!last || last.label !== label) out.push({ label, items: [ev] });
      else last.items.push(ev);
    });
    return out;
  }, [list]);

  const renderEvent = (ev: FeedEvent) => {
    const cat     = categoryOf(ev);
    const isAlert = cat === 'alert';
    const sev     = sevOf(ev);
    const warn    = sev === 'warning';
    const m       = getMeta(ev);
    const routine = !isAlert && (cat === 'nav'
      || (ev.type === 'tracked_event' && !ev.needsAttention)
      || ev.type === 'info');

    const accent = isAlert ? (warn ? '#E08600' : '#e11d48') : m.color;
    const evStyle: React.CSSProperties | undefined = isAlert
      ? {
          borderLeft: `3px solid ${accent}`,
          background:  warn ? 'rgba(224,134,0,0.09)'  : 'rgba(225,29,72,0.07)',
          borderColor: warn ? 'rgba(224,134,0,0.30)'  : 'rgba(225,29,72,0.28)',
        }
      : undefined;

    const rep = ev.clickCount && ev.clickCount > 1 ? ev.clickCount : undefined;
    const metaEntries = ev.metadata ? Object.entries(ev.metadata).slice(0, 3) : [];

    return (
      <div key={ev.id} className={`${s.ev} ${routine ? s.routine : ''}`} style={evStyle}>
        <span
          className={`${s.node} ${isAlert && !warn ? s.ring : ''}`}
          style={{ color: accent, borderColor: isAlert ? accent : undefined }}
        >{m.icon}</span>

        <div className={s.top}>
          <span className={s.user}>{ev.userName}</span>
          {m.badge && (
            <span className={s.badge} style={{ color: m.color, background: m.color + '1A' }}>{m.badge}</span>
          )}
          {rep && <span className={s.rep}>×{rep}</span>}
          {ev.page && <span className={s.page}>{ev.page}</span>}
          {ev.needsAttention && (
            <span className={s.attn} style={{ background: accent }}>Needs attention</span>
          )}
          <span className={s.ts}>{fmt(ev)}</span>
        </div>

        <div className={s.desc}>{describe(ev)}</div>

        {(ev.elementId || metaEntries.length > 0) && (
          <div className={s.meta}>
            {ev.elementId && <span>element <code>{ev.elementId}</code></span>}
            {metaEntries.map(([k, v]) => (
              <span key={k}>{k} <code>{String(v)}</code></span>
            ))}
          </div>
        )}
      </div>
    );
  };

  return (
    <div className={s.wrap}>
      {/* Filter toolbar */}
      <div className={s.toolbar}>
        <div className={s.chips}>
          {CATS.map(c => {
            const on = filter === c.id;
            return (
              <button
                key={c.id}
                className={`${s.chip} ${on ? s.chipOn : ''}`}
                style={on ? { background: c.color, borderColor: 'transparent', color: '#fff' } : undefined}
                onClick={() => setFilter(c.id)}
              >
                <span>{c.icon} {c.label}</span>
                <span className={s.chipCnt} style={on ? { background: 'rgba(255,255,255,0.28)', color: '#fff' } : undefined}>
                  {counts[c.id]}
                </span>
              </button>
            );
          })}
        </div>
        <label className={s.search}>
          <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4">
            <circle cx="11" cy="11" r="7" /><path d="m21 21-4.3-4.3" />
          </svg>
          <input
            className={s.searchInput}
            type="text"
            value={query}
            placeholder="Search user, page, element…"
            onChange={e => setQuery(e.target.value)}
          />
        </label>
      </div>

      {/* Feed */}
      <div className={s.feed}>
        {list.length === 0 ? (
          <div className={s.empty}>
            <div className={s.emptyIco}>📊</div>
            <div>
              {query
                ? `No activity matching “${query}”.`
                : filter !== 'all'
                  ? `No ${CATS.find(c => c.id === filter)?.label.toLowerCase()} activity yet.`
                  : selectedUserName
                    ? `No activity for ${selectedUserName}`
                    : 'Waiting for user activity…'}
            </div>
          </div>
        ) : (
          groups.map(g => (
            <React.Fragment key={g.label}>
              <div className={s.groupHead}>
                <span className={s.gLbl}>{g.label}</span>
                <span className={s.gLine} />
                <span className={s.gCnt}>{g.items.length}</span>
              </div>
              <div className={s.track}>
                {g.items.map(renderEvent)}
              </div>
            </React.Fragment>
          ))
        )}
      </div>
    </div>
  );
};
